import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { theme } from "../theme";

// ── Portfolio Chart (vývoj průměrné pozice portfolia v čase) ────────────────────
export default function PortfolioChart({ token, height = 180, days = 30, shopMissing = false, onDaysChange = null, marginBottom = 28 }) {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiFetch(`/portfolio/history?days=${days}`, token)
      .then(data => { if (alive) setPoints(data.points || []); })
      .catch(() => { if (alive) setPoints([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, days]);

  if (loading) return null; // dokud se načítá, nic (ať to neproblikává)

  // málo dat -> jemný prázdný stav
  if (!points || points.length < 2) {
    return (
      <div className="fadeUp" style={{
        background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard,
        padding: 24, marginBottom, boxShadow: theme.shadowSm,
      }}>
        <p style={{ fontSize: 16, fontWeight: 800, color: theme.ink, marginBottom: 12 }}>Vývoj průměrné pozice</p>
        <div style={{ textAlign: "center", padding: "24px 0", color: theme.mut, fontSize: 14 }}>
          {shopMissing
            ? "Bez nastaveného e-shopu nemáme co vynášet — pozice se neměří."
            : "Graf se zobrazí, jakmile nasbíráme data z několika kontrol."}
        </div>
      </div>
    );
  }

  // rozměry
  const w = 700, h = height, padL = 34, padR = 16, padT = 18, padB = 26;
  const positions = points.map(p => p.avg_position);
  const minPos = Math.min(...positions);
  const maxPos = Math.max(...positions);
  // rozsah s malou rezervou; pozor: nižší pozice = lepší, takže osu Y obrátíme (nahoře nejlepší)
  const lo = Math.max(1, Math.floor(minPos) - 1);
  const hi = Math.ceil(maxPos) + 1;
  const range = (hi - lo) || 1;

  const x = (i) => padL + (i / Math.max(points.length - 1, 1)) * (w - padL - padR);
  const y = (pos) => padT + ((pos - lo) / range) * (h - padT - padB); // vyšší pozice = níž

  const linePts = points.map((p, i) => `${x(i)},${y(p.avg_position)}`).join(" ");
  const areaPts = `${padL},${h - padB} ${linePts} ${x(points.length - 1)},${h - padB}`;

  // popisky osy Y (3 hodnoty: nejlepší, střed, nejhorší)
  const yLabels = [lo, Math.round((lo + hi) / 2), hi];

  // popisky osy X (první a poslední datum)
  const fmtDate = (d) => {
    const [, mo, dy] = d.split("-");
    return `${parseInt(dy)}.${parseInt(mo)}.`;
  };

  // trend za celé období (první vs. poslední bod)
  const first = points[0].avg_position;
  const last = points[points.length - 1].avg_position;
  const delta = first - last; // kladné = zlepšení
  const trendColor = delta > 0.05 ? theme.ok : delta < -0.05 ? theme.bad : theme.inkSoft;
  const trendTxt = delta > 0.05 ? `↑ o ${Math.abs(delta).toFixed(1)} lepší za období`
    : delta < -0.05 ? `↓ o ${Math.abs(delta).toFixed(1)} horší za období`
    : "beze změny za období";

  return (
    <div className="fadeUp" style={{
      background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard,
      padding: 24, marginBottom, boxShadow: theme.shadowSm,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <p style={{ fontSize: 16, fontWeight: 800, color: theme.ink }}>Vývoj průměrné pozice</p>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: trendColor, fontVariantNumeric: theme.tnum }}>{trendTxt}</span>
          {onDaysChange && (
            <select value={days} onChange={e => onDaysChange(Number(e.target.value))} style={{ padding: "6px 10px", borderRadius: theme.rBtn, border: `1px solid ${theme.line}`, background: theme.bg, color: theme.inkSoft, fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", outline: "none" }}>
              {[7, 30, 90].map(d => <option key={d} value={d}>{d} dní</option>)}
            </select>
          )}
        </div>
      </div>
      <div style={{ width: "100%", overflow: "hidden" }}>
        <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: "block" }}>
          <defs>
            <linearGradient id="pfFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.blue} stopOpacity="0.16" />
              <stop offset="100%" stopColor={theme.blue} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* vodorovné mřížky + popisky Y */}
          {yLabels.map((lbl, i) => {
            const yy = y(lbl);
            return (
              <g key={i}>
                <line x1={padL} y1={yy} x2={w - padR} y2={yy} stroke={theme.line} strokeWidth="1" />
                <text x={padL - 8} y={yy + 3} textAnchor="end" fontSize="10" fill={theme.mut} fontFamily="Geist, sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>#{lbl}</text>
              </g>
            );
          })}
          {/* plocha + čára */}
          <polygon points={areaPts} fill="url(#pfFill)" />
          <polyline points={linePts} fill="none" stroke={theme.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {/* body */}
          {points.map((p, i) => (
            <circle key={i} cx={x(i)} cy={y(p.avg_position)} r="3" fill={theme.blue} stroke="#fff" strokeWidth="1.5" />
          ))}
          {/* popisky X (první a poslední) */}
          <text x={padL} y={h - 8} textAnchor="start" fontSize="10" fill={theme.mut} fontFamily="Geist, sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(points[0].date)}</text>
          <text x={w - padR} y={h - 8} textAnchor="end" fontSize="10" fill={theme.mut} fontFamily="Geist, sans-serif" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(points[points.length - 1].date)}</text>
        </svg>
      </div>
      <p style={{ fontSize: 12, color: theme.mut, marginTop: 8, textAlign: "center" }}>
        Nižší pozice = lepší. Graf ukazuje průměr přes všechny sledované produkty.
      </p>
    </div>
  );
}
